import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    TextField,
    Button,
    Checkbox,
    FormControlLabel,
    CircularProgress,
    Box,
    InputAdornment,
    IconButton,
} from '@material-ui/core';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import EmailIcon from '@material-ui/icons/Email';
import LockIcon from '@material-ui/icons/Lock';
import CloseIcon from '@material-ui/icons/Close';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { Link } from 'react-router';
import { signIn, signInBefore } from '../../actions/authActions';
import { signUp } from '../../actions/authActions';
import { getInitialInfo } from '../../actions/userActions';
import { throttle } from '../../utils/functions';
import { dayjs } from '../../utils/dateTime';
import './AuthFlowDialog.scss';

class AuthFlowDialog extends React.Component {
    constructor(props) {
        super(props);
        // 如果传入了 initialStep，使用它；否则默认使用 'login'
        const initialStep = props.initialStep || 'login';
        this.state = {
            currentStep: initialStep, // 'login', 'signup', 'accountConfirm', 'connectWallet'
            loginErrors: {
                hasError: false,
                message: '',
            },
            signupErrors: {
                hasError: false,
                message: '',
            },
            registrationSuccess: false,
            // 登录表单字段
            loginForm: {
                identity: '',
                password: '',
            },
            loginFormErrors: {},
            // 注册表单字段
            signupForm: {
                email: '',
                username: '',
                password: '',
                iAgree: false,
            },
            signupFormErrors: {},
        };
        this.loginInfo = {};
        this.signupInfo = {};
    }

    componentDidMount() {
        // 初始化 throttle 函数
        if (this.props.signIn) {
            this.throtSignIn = throttle(this.props.signIn, 3000);
        }
        if (this.props.signInBefore) {
            this.throtSignInBefore = throttle(this.props.signInBefore, 3000);
        }
        if (this.props.signUp) {
            this.throtSignUp = throttle(this.props.signUp, 3000);
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        // 如果对话框从关闭变为打开，重置为 initialStep
        if (!this.props.open && nextProps.open && nextProps.initialStep) {
            this.setState({
                currentStep: nextProps.initialStep,
                loginErrors: { hasError: false, message: '' },
                signupErrors: { hasError: false, message: '' },
                registrationSuccess: false,
            });
        }

        // 如果 initialStep prop 变化，更新 currentStep
        if (nextProps.initialStep && nextProps.initialStep !== this.props.initialStep) {
            this.setState({ currentStep: nextProps.initialStep });
        }

        // 监听登录 / 注册请求完成
        if (this.props.auth.isLoading === true && nextProps.auth.isLoading === false) {
            if (nextProps.auth.errors.hasError) {
                // 登录/注册失败
                if (this.state.currentStep === 'login') {
                    this.setState({
                        loginErrors: {
                            hasError: true,
                            message: nextProps.auth.errors.message || 'Login failed. Please try again.',
                        },
                    });
                } else if (this.state.currentStep === 'signup') {
                    this.setState({
                        signupErrors: {
                            hasError: true,
                            message: nextProps.auth.errors.message || 'Registration failed. Please try again.',
                        },
                    });
                }
            } else {
                // 没有错误，认为本次登录 / 注册成功
                if (this.state.currentStep === 'login') {
                    this.setState({
                        currentStep: 'connectWallet',
                        loginErrors: { hasError: false, message: '' },
                    });
                    if (nextProps.auth.token) {
                        this.props.getInitialInfo && this.props.getInitialInfo(nextProps.auth.token);
                    }
                } else if (this.state.currentStep === 'signup') {
                    this.setState({
                        currentStep: 'connectWallet',
                        registrationSuccess: true,
                        signupErrors: { hasError: false, message: '' },
                    });
                    if (nextProps.auth.token) {
                        this.props.getInitialInfo && this.props.getInitialInfo(nextProps.auth.token);
                    }
                }
            }
        }
    }

    handleClose = () => {
        // 重置为初始步骤（如果已登录，重置为 accountConfirm；否则重置为 login）
        const initialStep = this.props.initialStep || 'login';
        this.setState({
            currentStep: initialStep,
            loginErrors: { hasError: false, message: '' },
            signupErrors: { hasError: false, message: '' },
            registrationSuccess: false,
            loginForm: { identity: '', password: '' },
            loginFormErrors: {},
            signupForm: { email: '', username: '', password: '', iAgree: false },
            signupFormErrors: {},
        });
        if (this.props.onClose) {
            this.props.onClose();
        }
    }

    // 验证登录表单
    validateLoginForm = () => {
        const errors = {};
        const { identity, password } = this.state.loginForm;

        if (!identity || identity.trim() === '') {
            errors.identity = 'This field is required';
        }

        if (!password || password.trim() === '') {
            errors.password = 'This field is required';
        }

        this.setState({ loginFormErrors: errors });
        return Object.keys(errors).length === 0;
    }

    // 验证注册表单
    validateSignupForm = () => {
        const errors = {};
        const { email, username, password, iAgree } = this.state.signupForm;

        // 邮箱验证
        if (!email || email.trim() === '') {
            errors.email = 'This field is required';
        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email)) {
            errors.email = 'Invalid email address';
        }

        // 用户名验证
        if (!username || username.trim() === '') {
            errors.username = 'This field is required';
        } else if (!/^[a-zA-Z0-9_]{5,}$/.test(username)) {
            errors.username = 'Username must be at least 5 characters: letters and numbers.';
        } else if (/^_+$/.test(username)) {
            errors.username = 'Username must be at least 5 characters: letters and numbers.';
        } else if (username.length > 25) {
            errors.username = 'Usernames must be less than 25 characters long.';
        }

        // 密码验证
        const passCheck = (string) => {
            const checkSpecial = /[*@!#%&()^~{}$]+/.test(string);
            const checkUpper = /[A-Z]+/.test(string);
            const checkLower = /[a-z]+/.test(string);
            return checkUpper && checkLower && checkSpecial;
        };

        if (!password || password.trim() === '') {
            errors.password = 'This field is required';
        } else if (password.length < 8) {
            errors.password = 'Passwords must have at least 8 characters';
        } else if (password.length > 30) {
            errors.password = 'Passwords cannot be longer than 30 characters';
        } else if (!passCheck(password)) {
            errors.password = 'Password must have at least one upper case letter and one of the following: !@#$%^&*()';
        }

        // 同意条款验证
        if (!iAgree) {
            errors.iAgree = "You will need to agree to NOIZ's Terms of Use and Privacy Policy";
        }

        this.setState({ signupFormErrors: errors });
        return Object.keys(errors).length === 0;
    }

    // 处理登录表单输入变化
    handleLoginInputChange = (field, value) => {
        this.setState({
            loginForm: {
                ...this.state.loginForm,
                [field]: value,
            },
            loginFormErrors: {
                ...this.state.loginFormErrors,
                [field]: '',
            },
            // 一旦用户开始修改表单，就把顶部的后端错误提示一起清掉
            loginErrors: { hasError: false, message: '' },
        });
    }

    // 处理注册表单输入变化
    handleSignupInputChange = (field, value) => {
        this.setState({
            signupForm: {
                ...this.state.signupForm,
                [field]: value,
            },
            signupFormErrors: {
                ...this.state.signupFormErrors,
                [field]: '',
            },
            // 修改表单时清除顶部后端错误提示
            signupErrors: { hasError: false, message: '' },
        });
    }

    // 处理登录表单提交
    handleLoginFormSubmit = (e) => {
        e.preventDefault();
        if (!this.validateLoginForm()) {
            return;
        }

        const { identity, password } = this.state.loginForm;
        const payload = {
            browserTimezone: dayjs.tz.guess(),
            identity: identity.toLowerCase(),
            password: password,
        };

        this.loginInfo = payload;
        // 弹框登录：直接调用 signIn，成功后进入链接钱包步骤
        if (this.throtSignIn) {
            this.throtSignIn(payload);
        } else if (this.props.signIn) {
            this.props.signIn(payload);
        }
    }

    // 处理注册表单提交
    handleSignUpFormSubmit = (e) => {
        e.preventDefault();
        if (!this.validateSignupForm()) {
            return;
        }

        const payload = { ...this.state.signupForm };
        this.signupInfo = payload;
        if (this.throtSignUp) {
            this.throtSignUp(payload);
        } else if (this.props.signUp) {
            this.props.signUp(payload);
        }
    }

    handleSwitchToSignUp = () => {
        this.setState({
            currentStep: 'signup',
            loginErrors: { hasError: false, message: '' },
            loginFormErrors: {},
        });
    }

    handleSwitchToLogin = () => {
        this.setState({
            currentStep: 'login',
            signupErrors: { hasError: false, message: '' },
            signupFormErrors: {},
        });
    }

    handleConfirmAccount = () => {
        // 点击已登录账号时，不直接关闭弹框和下单
        // 而是进入“连接钱包 / 绑定账号”步骤
        this.setState({
            currentStep: 'connectWallet',
            loginErrors: { hasError: false, message: '' },
        });
    }

    handleUseAnotherAccount = () => {
        this.setState({ currentStep: 'login' });
    }

    handleConnectOtherWallet = () => {
        // 连接其他钱包的逻辑
        this.handleClose();
        if (this.props.onWalletConnected) {
            this.props.onWalletConnected('other');
        }
    }

    handleGenerateNoizWallet = () => {
        // 生成Noiz钱包的逻辑
        this.handleClose();
        if (this.props.onWalletConnected) {
            this.props.onWalletConnected('noiz');
        }
    }

    getInitials = (username) => {
        if (!username) return 'U';
        return username.charAt(1).toUpperCase();
    }

    // 通用弹框渲染方法
    renderDialog = (title, content, showFooterLinks = false, showContactText = false) => {
        return (
            <>
                <DialogTitle className="dialog-title">
                    <Typography variant="h6" className="title-text">
                        {title}
                    </Typography>
                    <IconButton
                        onClick={this.handleClose}
                        className="close-button"
                        size="small"
                        aria-label="close"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent className="dialog-content">
                    {content}
                </DialogContent>

                <DialogActions className="dialog-actions">
                    {showFooterLinks && (
                        <>
                            <div className="links-container">
                                <Link
                                    to="/terms-of-use"
                                    className="link-item"
                                    target="_blank"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    Terms of Use
                                </Link>
                                <Link
                                    to="/privacy-policy"
                                    className="link-item"
                                    target="_blank"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    Privacy Policy
                                </Link>
                            </div>
                        </>
                    )}
                    {showContactText && (
                        <Typography variant="body2" className="contact-text">
                            Contact us at{' '}
                            <a
                                href="mailto:support@noiz.gg"
                                className="contact-link"
                                onClick={(e) => e.stopPropagation()}
                            >
                                support@noiz.gg
                            </a>
                        </Typography>
                    )}
                </DialogActions>
            </>
        );
    }

    renderLoginDialog = () => {
        const { loginErrors, loginForm, loginFormErrors } = this.state;
        const { auth } = this.props;
        const isLoading = auth && auth.isLoading;

        const content = (
            <>
                <Typography variant="body2" className="description-text">
                    Please enter your account and password for login
                </Typography>

                <form onSubmit={this.handleLoginFormSubmit} className="auth-form">
                    <TextField
                        fullWidth
                        id="login-identity"
                        label="Email or Username"
                        type="text"
                        value={loginForm.identity}
                        onChange={(e) => this.handleLoginInputChange('identity', e.target.value)}
                        error={!!loginFormErrors.identity}
                        helperText={loginFormErrors.identity}
                        disabled={isLoading}
                        margin="dense"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <AccountCircleIcon sx={{ color: '#9b9b9b', fontSize: 18 }} />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <TextField
                        fullWidth
                        id="login-password"
                        label="Password"
                        type="password"
                        value={loginForm.password}
                        onChange={(e) => this.handleLoginInputChange('password', e.target.value)}
                        error={!!loginFormErrors.password}
                        helperText={loginFormErrors.password}
                        disabled={isLoading}
                        margin="dense"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LockIcon sx={{ color: '#9b9b9b', fontSize: 18 }} />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Box mt={0.5} mb={1.5} display="flex" justifyContent="flex-end" alignItems="center">
                        <Link
                            to="/recover"
                            href="/recover"
                            onClick={(e) => e.stopPropagation()}
                            style={{ color: '#00bcd4', textDecoration: 'none', fontSize: '13px' }}
                        >
                            Forgot your password?
                        </Link>
                    </Box>

                    {loginErrors.hasError && (
                        <Box mb={2}>
                            <Typography variant="body2" className="error-message">
                                {loginErrors.message}
                            </Typography>
                        </Box>
                    )}

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        className="form-submit-button"
                        disabled={isLoading}
                        style={{
                            marginTop: '10px',
                            backgroundColor: '#ff5b5b',
                            color: '#fff',
                            padding: '12px',
                            fontSize: '14px',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                        }}
                    >
                        {isLoading ? (
                            <CircularProgress size={20} style={{ color: '#fff' }} />
                        ) : (
                            'SIGN IN'
                        )}
                    </Button>
                </form>

                <div className="switch-link-container">
                    <button
                        onClick={this.handleSwitchToSignUp}
                        className="switch-link-button"
                    >
                        New User Account Registration
                    </button>
                </div>
            </>
        );

        return this.renderDialog('Log in to Noiz for USDO Payment', content);
    }

    renderSignUpDialog = () => {
        const { signupErrors, signupForm, signupFormErrors } = this.state;
        const { auth } = this.props;
        const isLoading = auth && auth.isLoading;

        const content = (
            <>
                <Typography variant="body2" className="description-text">
                    Register an account to manage your USDO and digital assets
                </Typography>

                <form onSubmit={this.handleSignUpFormSubmit} className="auth-form">
                    <TextField
                        fullWidth
                        id="signup-email"
                        label="Email"
                        type="text"
                        value={signupForm.email}
                        onChange={(e) => this.handleSignupInputChange('email', e.target.value)}
                        error={!!signupFormErrors.email}
                        helperText={signupFormErrors.email}
                        disabled={isLoading}
                        margin="dense"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <EmailIcon sx={{ color: '#9b9b9b', fontSize: 18 }} />
                                </InputAdornment>
                            ),
                            style: { color: '#fff' },
                        }}
                        InputLabelProps={{
                            style: { color: '#c0b5cc' },
                        }}
                        FormHelperTextProps={{
                            style: { color: '#ff4444' },
                        }}
                    />

                    <TextField
                        fullWidth
                        id="signup-username"
                        label="Noiz Username"
                        type="text"
                        value={signupForm.username}
                        onChange={(e) => this.handleSignupInputChange('username', e.target.value)}
                        error={!!signupFormErrors.username}
                        helperText={signupFormErrors.username}
                        disabled={isLoading}
                        margin="dense"
                        inputProps={{
                            pattern: '[A-Za-z0-9_]{5,}',
                            title: 'Use at least 5 characters: letters and numbers',
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <AccountCircleIcon sx={{ color: '#9b9b9b', fontSize: 18 }} />
                                </InputAdornment>
                            ),
                            style: { color: '#fff' },
                        }}
                        InputLabelProps={{
                            style: { color: '#c0b5cc' },
                        }}
                        FormHelperTextProps={{
                            style: { color: '#ff4444' },
                        }}
                    />

                    <TextField
                        fullWidth
                        id="signup-password"
                        label="Password"
                        type="password"
                        value={signupForm.password}
                        onChange={(e) => this.handleSignupInputChange('password', e.target.value)}
                        error={!!signupFormErrors.password}
                        helperText={signupFormErrors.password}
                        disabled={isLoading}
                        margin="dense"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LockIcon sx={{ color: '#9b9b9b', fontSize: 18 }} />
                                </InputAdornment>
                            ),
                            style: { color: '#fff' },
                        }}
                        InputLabelProps={{
                            style: { color: '#c0b5cc' },
                        }}
                        FormHelperTextProps={{
                            style: { color: '#ff4444' },
                        }}
                    />

                    <Box mt={2} mb={1}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={signupForm.iAgree}
                                    onChange={(e) => this.handleSignupInputChange('iAgree', e.target.checked)}
                                    disabled={isLoading}
                                    style={{ color: signupFormErrors.iAgree ? '#ff4444' : '#00bcd4' }}
                                />
                            }
                            label={
                                <Typography variant="body2" style={{ color: '#fff', fontSize: '14px' }}>
                                    By registering, you agree to NOIZ's{' '}
                                    <Link
                                        to="/terms-of-use"
                                        href="/terms-of-use"
                                        target="_blank"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ color: '#00bcd4', textDecoration: 'none' }}
                                    >
                                        Terms of Use
                                    </Link>
                                    ,{' '}
                                    <Link
                                        to="/privacy-policy"
                                        href="/privacy-policy"
                                        target="_blank"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ color: '#00bcd4', textDecoration: 'none' }}
                                    >
                                        Privacy Policy
                                    </Link>
                                    {' '}and{' '}
                                    <Link
                                        to="/privacy-policy-update"
                                        href="/privacy-policy-update"
                                        target="_blank"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ color: '#00bcd4', textDecoration: 'none' }}
                                    >
                                        updated terms
                                    </Link>
                                </Typography>
                            }
                            style={{ alignItems: 'flex-start' }}
                        />
                        {signupFormErrors.iAgree && (
                            <Typography variant="caption" style={{ color: '#ff4444', display: 'block', marginTop: '4px' }}>
                                {signupFormErrors.iAgree}
                            </Typography>
                        )}
                    </Box>

                    {signupErrors.hasError && (
                        <Box mb={2}>
                            <Typography variant="body2" className="error-message">
                                {signupErrors.message}
                            </Typography>
                        </Box>
                    )}

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        className="form-submit-button"
                        disabled={isLoading}
                        style={{
                            marginTop: '10px',
                            backgroundColor: '#ff5b5b',
                            color: '#fff',
                            padding: '12px',
                            fontSize: '14px',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                        }}
                    >
                        {isLoading ? (
                            <CircularProgress size={20} style={{ color: '#fff' }} />
                        ) : (
                            'SIGN UP'
                        )}
                    </Button>
                </form>

                <div className="switch-link-container">
                    <span className="switch-link-text">Already have an account? </span>
                    <button
                        type="button"
                        onClick={this.handleSwitchToLogin}
                        className="switch-link-button"
                    >
                        Sign In <ArrowForwardIcon style={{ fontSize: 14, marginLeft: 2 }} />
                    </button>
                </div>
            </>
        );

        return this.renderDialog('Register & Purchase USDO', content, false, true);
    }

    renderAccountConfirmDialog = () => {
        const { auth } = this.props;
        const content = (
            <>
                <Typography variant="body2" className="description-text">
                    Please confirm the account you wish to log in to
                </Typography>

                {/* 当前账户 */}
                {auth && auth.user && (
                    <div
                        className="account-item"
                        onClick={this.handleConfirmAccount}
                    >
                        <div className="account-avatar">
                            {this.getInitials(auth.user.username)}
                        </div>
                        <div className="account-info">
                            <Typography variant="body1" className="account-name">
                                {auth.user.username || auth.user.displayName}
                            </Typography>
                            <Typography variant="body2" className="account-email">
                                {auth.user.email}
                            </Typography>
                        </div>
                    </div>
                )}

                {/* 分隔线 */}
                <div className="divider" />

                {/* 使用其他账户 */}
                <div
                    className="use-another-account"
                    onClick={this.handleUseAnotherAccount}
                >
                    <div className="another-account-icon">
                        <i className="fa fa-user icon" />
                    </div>
                    <Typography variant="body1" className="another-account-text">
                        Use Another Account
                    </Typography>
                </div>
            </>
        );

        return this.renderDialog('Log in to Noiz for USDO Payment', content, true, true);
    }

    renderConnectWalletDialog = () => {
        const { registrationSuccess } = this.state;
        const content = (
            <>
                {registrationSuccess && (
                    <Typography variant="body2" className="success-message">
                        Registration Successful!
                    </Typography>
                )}

                <Typography variant="body2" className="description-text">
                    Select the wallet you wish to connect, or we can generate one for you.
                </Typography>

                {/* Connect Other Wallet */}
                <div
                    className="wallet-option"
                    onClick={this.handleConnectOtherWallet}
                >
                    <div className="wallet-option-content">
                        <div className="wallet-icon">
                            <i className="fa fa-link icon" />
                        </div>
                        <Typography variant="body1" className="wallet-text">
                            Connect Other Wallet
                        </Typography>
                    </div>
                    <i className="fa fa-arrow-right wallet-arrow" />
                </div>

                {/* Generate Noiz Digital Wallet */}
                <div
                    className="wallet-option"
                    onClick={this.handleGenerateNoizWallet}
                >
                    <div className="wallet-option-content">
                        <div className="wallet-icon">
                            <i className="fa fa-credit-card icon" />
                        </div>
                        <Typography variant="body1" className="wallet-text">
                            Generate Noiz Digital Wallet
                        </Typography>
                    </div>
                    <i className="fa fa-arrow-right wallet-arrow" />
                </div>
            </>
        );

        return this.renderDialog('Connect Your Wallet', content, false);
    }

    render() {
        const { open } = this.props;
        const { currentStep } = this.state;

        console.log('AuthFlowDialog render - open:', open, 'currentStep:', currentStep);

        if (!open) {
            console.log('AuthFlowDialog: open is false, returning null');
            return null;
        }

        console.log('AuthFlowDialog: rendering Dialog');

        return (
            <Dialog
                open={open}
                onClose={this.handleClose}
                disablePortal={false}
                maxWidth="sm"
                fullWidth={false}
                className="auth-flow-dialog"
                style={{ zIndex: 10000 }}
                PaperProps={{
                    style: {
                        backgroundColor: '#322d38',
                        borderRadius: '8px',
                        padding: '20px',
                        minWidth: '400px',
                        maxWidth: '500px',
                        zIndex: 10001,
                    },
                }}
                BackdropProps={{
                    style: {
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        zIndex: 9999,
                    },
                }}
            >
                {currentStep === 'login' && this.renderLoginDialog()}
                {currentStep === 'signup' && this.renderSignUpDialog()}
                {currentStep === 'accountConfirm' && this.renderAccountConfirmDialog()}
                {currentStep === 'connectWallet' && this.renderConnectWalletDialog()}
            </Dialog>
        );
    }
}

const mapStateToProps = (state) => ({
    auth: state.auth,
    user: state.user,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
    signIn,
    signInBefore,
    signUp,
    getInitialInfo,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(AuthFlowDialog);
